/**
 * The requests of a metric and the figures read from their answers (#2175 B1).
 * Port of the pure part of mobile-management/src/features/metrics/useMetricData.ts
 * (#1989 G2, G3, C13, D10, D13, D15); `useMetricData.ts` wraps it in queries.
 *
 * - The hub card and the metric detail read the same window, the twelve
 *   months ending at the period's last month, so they share one request; the
 *   period's total is taken from the months of the period inside it.
 * - A metric with a server summary (the sickness rate) reads its period total
 *   from a second request for exactly the period when the window differs.
 * - A forward metric reads its own fixed window and ignores the period.
 * - A series without its own comparison is read a year further back and each
 *   month compares with the same month a year earlier.
 * - Slices are one request per location or group, ranked on the client: up to
 *   24 are requested and the top 12 shown; beyond 24 the first 12 are
 *   requested and the list says so.
 */

import { withYearAgo } from "./adapters";
import { metricDelta, periodTotals, pointsInRange, shiftMonth, type MetricDelta } from "./period";
import type { MetricDefinition, MetricPoint, MetricRequestParams } from "./types";

/** The scope of a request as the cockpit endpoints take it (comma lists). */
export interface MetricScopeParams {
  childcare_id?: string;
  group_id?: string;
}

export type MetricQueryParams = Record<string, string | boolean>;

/** One GET of a metric. */
export interface MetricRequest {
  path: string;
  params: MetricQueryParams;
}

/** The params of one request: the window, the metric's own params, the scope it takes. */
export function metricParams(
  def: MetricDefinition,
  from: string,
  to: string,
  scope: MetricScopeParams,
  extra: MetricRequestParams = {},
): MetricQueryParams {
  const params: MetricQueryParams = { from, to, ...(def.params ?? {}), ...extra };
  if (def.scope.location && scope.childcare_id) params.childcare_id = scope.childcare_id;
  if (def.scope.group && scope.group_id) params.group_id = scope.group_id;
  return params;
}

/**
 * The params a metric carries besides period and scope: the "incl. child sick"
 * switch of the sickness rate (off by default, as on the phone).
 */
export function metricExtraParams(
  def: MetricDefinition,
  options: { includeChildSick?: boolean } = {},
): MetricRequestParams {
  return def.childSickToggle ? { include_child_sick: options.includeChildSick ?? false } : {};
}

/** The first month of the chart window: twelve months ending at `to`, or `from` if earlier. */
export function chartWindowFrom(from: string, to: string): string {
  const twelve = shiftMonth(to, -11);
  return twelve < from ? twelve : from;
}

/**
 * The month the requests start at: a year earlier for a series without its own
 * comparison, unless the endpoint returns that year itself
 * (`compare_to: previous_year`, the monthly trend since #2192).
 */
export function readFrom(def: MetricDefinition, windowFrom: string): string {
  return def.yearAgoFromSeries && def.params?.compare_to !== "previous_year" ? shiftMonth(windowFrom, -12) : windowFrom;
}

export interface MetricSummary {
  /** The months of the period (all months of a forward metric). */
  periodPoints: MetricPoint[];
  total: number | null;
  previousTotal: number | null;
  delta: MetricDelta | undefined;
  /** The month a stock total stands at. */
  lastPeriod: string | undefined;
}

/**
 * Totals of the months in [from, to] by the metric's aggregate kind; a forward
 * metric totals its whole window. `server` overrides the total when the metric
 * reads it from a summary.
 */
export function summarize(
  def: MetricDefinition,
  points: MetricPoint[],
  from: string,
  to: string,
  server?: { total: number | null; previous: number | null } | null,
): MetricSummary {
  const periodPoints = def.forward ? points : pointsInRange(points, from, to);
  const totals = periodTotals(def.aggregate, periodPoints);
  const total = server ? server.total : totals.total;
  const previousTotal = def.forward ? null : server ? server.previous : totals.previous;
  return {
    periodPoints,
    total,
    previousTotal,
    delta: def.forward ? undefined : metricDelta(def.unit, total, previousTotal),
    lastPeriod: totals.lastPeriod,
  };
}

/** Every request's points in one month order; a month read twice keeps the later read. */
export function mergePoints(lists: MetricPoint[][]): MetricPoint[] {
  const byMonth = new Map<string, MetricPoint>();
  for (const list of lists) for (const p of list) if (p.period) byMonth.set(p.period, p);
  return [...byMonth.values()].sort((a, b) => a.period.localeCompare(b.period));
}

/**
 * The requests of a metric for a chart window and a period, the summary
 * request apart when one is needed.
 */
export function buildRequests(
  def: MetricDefinition,
  windowFrom: string,
  from: string,
  to: string,
  scope: MetricScopeParams,
  extra: MetricRequestParams = {},
): { chart: MetricRequest[]; summary: MetricRequest | null } {
  const windows = def.requests ? def.requests(windowFrom, to) : [{ from: windowFrom, to }];
  const chart = windows.map((w) => {
    const { from: wFrom, to: wTo, ...rest } = w as MetricRequestParams & { from: string; to: string };
    return { path: def.path, params: metricParams(def, wFrom, wTo, scope, { ...rest, ...extra }) };
  });
  const needsSummary = !!def.summary && !def.requests && (windowFrom !== from || chart.length !== 1);
  return {
    chart,
    summary: needsSummary ? { path: def.path, params: metricParams(def, from, to, scope, extra) } : null,
  };
}

/** The request plan of one metric series: chart requests, then the summary one. */
export interface SeriesPlan {
  windowFrom: string;
  chart: MetricRequest[];
  summary: MetricRequest | null;
  /** chart requests followed by the summary request, the order of the answers. */
  all: MetricRequest[];
}

export function planSeries(
  def: MetricDefinition,
  from: string,
  to: string,
  scope: MetricScopeParams,
  extra: MetricRequestParams = {},
): SeriesPlan {
  const windowFrom = chartWindowFrom(from, to);
  const built = buildRequests(def, readFrom(def, windowFrom), from, to, scope, extra);
  return {
    windowFrom,
    chart: built.chart,
    summary: built.summary,
    all: built.summary ? [...built.chart, built.summary] : built.chart,
  };
}

export interface SeriesFigures extends MetricSummary {
  /** The months of the chart window. */
  points: MetricPoint[];
  /** The last twelve values with data, oldest first. */
  sparkline: number[];
}

/**
 * The figures of a series from the answers of its plan, in plan order
 * (`undefined` for a request that has no answer yet).
 */
export function deriveSeries(
  def: MetricDefinition,
  plan: SeriesPlan,
  answers: readonly unknown[],
  from: string,
  to: string,
): SeriesFigures {
  const chartData = answers.slice(0, plan.chart.length);
  const summaryData = plan.summary ? answers[plan.chart.length] : chartData[0];
  const merged = mergePoints(chartData.map((d) => (d ? def.adapt(d) : [])));
  const points = def.yearAgoFromSeries
    ? withYearAgo(merged).filter((p) => p.period >= plan.windowFrom)
    : merged;
  const server = def.summary && summaryData ? def.summary(summaryData) : null;
  const summary = summarize(def, points, from, to, server);
  const sparkline = points
    .map((p) => p.value)
    .filter((v): v is number => v !== null && Number.isFinite(v))
    .slice(-12);
  return { points, sparkline, ...summary };
}

export type SliceMode = "location" | "group";

/** The most slices a list shows. */
export const SLICE_GROUP_CAP = 12;
/** The most slices a fan-out requests (the top twelve are shown). */
export const SLICE_FETCH_CAP = 24;

export interface SliceTarget {
  id: string;
  name: string;
}

export interface SliceRow {
  key: string;
  label: string;
  total: number | null;
  previousTotal: number | null;
  delta: MetricDelta | undefined;
  points: MetricPoint[];
}

/**
 * The slice targets of a mode inside the scope: the selected locations (or all),
 * the groups the scope allows (all when `allowedGroupIds` is null).
 */
export function sliceTargets(
  mode: SliceMode | null,
  locations: readonly SliceTarget[],
  groups: readonly SliceTarget[],
  scope: { locationIds: readonly string[]; allowedGroupIds: ReadonlySet<string> | null },
): SliceTarget[] {
  if (mode === "location") {
    const all = locations.map((c) => ({ id: c.id, name: c.name }));
    return scope.locationIds.length > 0 ? all.filter((c) => scope.locationIds.includes(c.id)) : all;
  }
  if (mode === "group") {
    return groups
      .filter((g) => scope.allowedGroupIds === null || scope.allowedGroupIds.has(g.id))
      .map((g) => ({ id: g.id, name: g.name }));
  }
  return [];
}

export interface SlicePlan {
  /** The targets that are requested (at most 24; the first 12 beyond that). */
  fetched: SliceTarget[];
  /** Requests per fetched target, in target order. */
  requests: MetricRequest[][];
  /** Every request, flattened in target order. */
  flat: MetricRequest[];
  truncated: "top" | "first" | null;
  optionCount: number;
}

/** One request set per location or group: the endpoint's own filter, over exactly the period. */
export function planSlices(
  def: MetricDefinition,
  mode: SliceMode | null,
  targets: readonly SliceTarget[],
  from: string,
  to: string,
  extra: MetricRequestParams = {},
): SlicePlan {
  const fetched = targets.length > SLICE_FETCH_CAP ? targets.slice(0, SLICE_GROUP_CAP) : [...targets];
  const requests = fetched.map(
    (t) =>
      buildRequests(def, from, from, to, mode === "location" ? { childcare_id: t.id } : { group_id: t.id }, extra)
        .chart,
  );
  const truncated: "top" | "first" | null =
    targets.length > SLICE_FETCH_CAP ? "first" : targets.length > SLICE_GROUP_CAP ? "top" : null;
  return { fetched, requests, flat: requests.flat(), truncated, optionCount: targets.length };
}

/**
 * The slice rows from the answers of a plan (flat order): each target's total
 * over the period, ranked by value then label, the top twelve.
 */
export function rankSlices(
  def: MetricDefinition,
  plan: SlicePlan,
  answers: readonly unknown[],
  from: string,
  to: string,
): SliceRow[] {
  let cursor = 0;
  const ranked: SliceRow[] = plan.fetched
    .map((t, i) => {
      const n = plan.requests[i].length;
      const data = answers.slice(cursor, cursor + n);
      cursor += n;
      const points = mergePoints(data.map((d) => (d ? def.adapt(d) : [])));
      // A server summary per slice is the request itself (window = period).
      const server = def.summary && data[0] ? def.summary(data[0]) : null;
      const summary = summarize(def, points, from, to, server);
      return {
        key: t.id,
        label: t.name,
        total: summary.total,
        previousTotal: summary.previousTotal,
        delta: summary.delta,
        points: summary.periodPoints,
      };
    })
    .sort((a, b) => (b.total ?? -Infinity) - (a.total ?? -Infinity) || a.label.localeCompare(b.label));
  return ranked.slice(0, SLICE_GROUP_CAP);
}

/** A row's share of the slices' sum; a mean has no share. */
export function sliceShare(def: MetricDefinition, row: SliceRow, rows: readonly SliceRow[]): number | null {
  if (def.aggregate === "mean" || row.total === null) return null;
  const sum = rows.reduce((acc, r) => acc + (r.total ?? 0), 0);
  return sum > 0 ? row.total / sum : null;
}
